import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TOOFI_CARD_VARIANT_CLASS } from "@/lib/toofi-ux-tokens";
import { cn } from "@/lib/utils";
import type { ToofiArtifactRecordIndexItem } from "@/lib/toofi-artifact-types";
import { ArrowUpRight, BookOpenText, Pin, PinOff, Brain, Trash2 } from "lucide-react";

const KIND_LABELS: Record<ToofiArtifactRecordIndexItem["kind"], string> = {
  plan_overview: "Plan",
  plan_milestones: "Milestones",
  daily_plan: "Daily plan",
  plan_checkin: "Check-in",
  note: "Note",
  mcq: "Quiz",
  essay_prompt: "Essay",
  comparison: "Compare",
  worked_example: "Example",
  summary: "Summary",
  table: "Table",
  formula_sheet: "Formula sheet",
};

export function ToofiArtifactCard({
  artifact,
  onOpen,
  onDelete,
  onSetPinned,
}: {
  artifact: ToofiArtifactRecordIndexItem;
  onOpen: (artifactId: string) => void;
  onDelete: (artifactId: string) => void;
  onSetPinned: (artifactId: string, pinned: boolean) => void;
}) {
  return (
    <div className={cn(TOOFI_CARD_VARIANT_CLASS.artifact, "p-3 backdrop-blur-xl")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="rounded-full border-slate-200/80 bg-white/80 px-2.5 py-1 text-[9px] uppercase tracking-[0.16em] text-slate-500">
              {KIND_LABELS[artifact.kind]}
            </Badge>
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em]",
                artifact.scope === "reader" ? "bg-slate-100 text-slate-700" : "bg-amber-50 text-amber-700",
              )}
            >
              {artifact.scope === "reader" ? <BookOpenText className="h-3 w-3" /> : <Brain className="h-3 w-3" />}
              {artifact.scope}
            </span>
          </div>
          <p className="mt-2 truncate text-[13px] font-semibold tracking-[-0.02em] text-slate-900">{artifact.title}</p>
          <p className="mt-1 line-clamp-2 text-[11px] leading-5 text-slate-500">{artifact.preview}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
            {artifact.sourcePageLabel ? <span>{artifact.sourcePageLabel}</span> : null}
            <span>{new Date(artifact.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "h-8 w-8 rounded-full hover:bg-white/90",
              artifact.pinned ? "text-amber-700" : "text-slate-400 hover:text-slate-700",
            )}
            onClick={() => onSetPinned(artifact.id, !artifact.pinned)}
            aria-label={artifact.pinned ? "Unpin artifact" : "Pin artifact"}
          >
            {artifact.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-rose-400 hover:bg-rose-50 hover:text-rose-600"
            onClick={() => onDelete(artifact.id)}
            aria-label="Delete artifact"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-8 rounded-full border-slate-200/80 bg-white/84 px-3 text-[11px] text-slate-700 hover:bg-white"
          onClick={() => onOpen(artifact.id)}
        >
          <ArrowUpRight className="mr-1.5 h-3.5 w-3.5" />
          Open
        </Button>
      </div>
    </div>
  );
}
